/**
 * Composant officiel pour le PDF (largeur fixe 800px, calque exact du modèle 2i).
 * Toujours en LIGHT mode (le PDF imprimé/téléchargé doit être lisible sur papier).
 */
import React from 'react';
import { ReleveData } from '../services/portailApi';

function getMention(moy: number | null): string {
  if (moy === null || moy === undefined) return '';
  if (moy >= 16) return 'Très Bien';
  if (moy >= 14) return 'Bien';
  if (moy >= 12) return 'Assez Bien';
  if (moy >= 10) return 'Passable';
  return '';
}

function fmt(v: number | null | undefined, dec = 2): string {
  if (v === null || v === undefined) return '';
  return Number(v).toFixed(dec).replace('.', ',');
}

interface Props {
  data: ReleveData;
  codeVerification?: string | null;
}

export const ReleveOfficiel: React.FC<Props> = ({ data, codeVerification }) => {
  const etu = data.etudiant;
  const res = data.resultat_semestre;
  const ues = data.ues || [];
  const moyenneFinale = res?.moyenne_semestre;
  const mention = getMention(moyenneFinale ?? null);
  const valide = res?.valide;
  const dateImpression = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  // Infos institut selon le campus, incluses dans les données du relevé (2026-10-10).
  const institut = data.institut || {};
  const infoTel = institut.telephone || '(+242) 06 855 67 94 / 06 736 10 00 / 06 455 3800';
  const infoEmail = institut.email || 'contact2i@institut2i.com';
  const infoAdresse = institut.adresse_rue || '06, RUE LI-LELEMB BASE INDUSTRIELLE';
  const infoVille = institut.ville || 'Pointe-Noire';
  const infoAgrement = (institut as any).numero_agrement || "Agrément définitif de l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021";
  const infoSite = (institut as any).site_web || 'www.institut2i.com';
  const infoVilleMaj = infoVille.toUpperCase();
  const isBZ = institut.deployment === 'bz';
  // Un seul « Le » : le réglage institut peut déjà commencer par « Le » (le
  // placeholder le suggère), d'où « Le Le Chef… » si on préfixe à l'aveugle.
  const chefTitreBase = (institut.chef_scolarite_titre || 'Chef de Service de la Scolarité et Examens').trim();
  const chefTitre = /^le\s/i.test(chefTitreBase) ? chefTitreBase : `Le ${chefTitreBase}`;
  const chefNom = institut.chef_scolarite || '';

  const qrUrl = codeVerification
    ? `https://api.qrserver.com/v1/create-qr-code/?size=130x130&margin=0&data=${encodeURIComponent(window.location.origin + '/portail/verifier/' + codeVerification)}`
    : null;

  return (
    <div className="bg-white text-gray-900 w-[800px] relative" style={{ fontFamily: 'Calibri, Arial, sans-serif' }}>
      {/* Filigrane PROVISOIRE */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden z-0">
        <div className="text-[140px] font-black text-red-200/40 -rotate-[28deg] whitespace-nowrap tracking-tight">
          PROVISOIRE
        </div>
      </div>

      <div className="relative z-10 p-7 text-[12px] leading-tight">
        {/* En-tête */}
        <div className="flex justify-between items-start border-b-2 border-blue-700 pb-4 mb-4">
          <div className="text-[10px] uppercase font-bold text-gray-800 leading-snug max-w-[60%]">
            <div>MINISTÈRE DE L'ENSEIGNEMENT SUPERIEUR</div>
            <div className="mt-2 inline-block px-2 py-1 border border-blue-700 bg-blue-50">
              INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE 2I
            </div>
            <div className="mt-2 normal-case text-[10px] font-normal space-y-0.5">
              <div><span className="underline">Tél.</span> :  {infoTel}</div>
              <div><span className="underline">E-mail</span> :  {infoEmail}</div>
              <div><span className="underline">Adresse</span> : {infoAdresse}</div>
              <div className="ml-12">Ville : {infoVilleMaj}</div>
              <div><span className="underline">Site</span> : {infoSite}</div>
              <div className="italic font-semibold text-blue-900">{infoAgrement}</div>
            </div>
          </div>
          <div className="text-right text-[10px] uppercase font-bold text-gray-800">
            <div>RÉPUBLIQUE DU CONGO</div>
            <div className="italic font-normal text-[10px]">Unité-Travail-Progrès</div>
            <img src="/logo.png" alt="2i" className="w-20 h-20 mt-2 mx-auto" crossOrigin="anonymous" />
            <div className="italic font-semibold text-[8px] text-gray-700 normal-case mt-0.5">S'instruire pour mieux Servir</div>
          </div>
        </div>

        {/* Titre RELEVE DE NOTES + Semestre */}
        <div className="flex justify-between items-center mb-4">
          <div className="bg-blue-700 text-white px-8 py-2.5 font-bold text-xl tracking-wider rounded shadow-md">
            RELEVE DE NOTES
          </div>
          <div className="bg-blue-700 text-white px-5 py-2.5 font-bold rounded shadow-md text-base">
            Semestre {data.semestre}
          </div>
        </div>

        {/* Infos session */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="border border-gray-400 px-3 py-1.5 text-[12px] bg-gray-50">
            Année Académique <strong>: {data.annee_academique}</strong>
          </div>
          <div className="border border-gray-400 px-3 py-1.5 text-[12px] bg-gray-50">
            Cycle <strong>: {etu.cycle || 'LICENCE PROFESSIONNELLE(LP)'}</strong>
          </div>
        </div>

        {/* Bloc identité étudiant + QR/photo */}
        <div className="flex justify-between items-start gap-4 mb-3">
          <div className="space-y-1 flex-1 text-[12px]">
            <div>Matricule : <strong>{etu.matricule}</strong></div>
            <div>Noms : <strong>{etu.nom}</strong></div>
            <div>Prénoms : <strong>{etu.prenoms}</strong></div>
            <div>Date Nce : <strong>{etu.date_naissance || '—'}</strong>{etu.lieu_naissance ? <> à <strong>{etu.lieu_naissance}</strong></> : null}</div>
            <div>Niveau : <strong>{etu.classe || etu.niveau}</strong> &nbsp; Option: <strong>{etu.option || '—'}</strong></div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-start gap-2">
              {qrUrl && (
                <img src={qrUrl} alt="QR" className="w-[110px] h-[110px] border border-gray-300" crossOrigin="anonymous" />
              )}
              {etu.photo_url && (
                <img src={etu.photo_url} alt="" className="w-[100px] h-[120px] object-cover border border-gray-300" crossOrigin="anonymous" />
              )}
            </div>
            {codeVerification && (
              <div className="text-[10px] font-mono text-gray-700 mt-1">2I-2026-REL-{codeVerification}</div>
            )}
          </div>
        </div>

        {/* Tableau des notes */}
        <table className="w-full border-collapse border border-gray-400 text-[11px]" style={{ verticalAlign: 'middle' }}>
          <thead>
            <tr className="bg-gray-200">
              <th className="border border-gray-400 px-2 py-1.5 text-left align-middle">Modules/U.E</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[42px] text-center align-middle">D.1</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[42px] text-center align-middle">D.2</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[55px] text-center align-middle">Moy.D<br/>(40%)</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[50px] text-center align-middle">Examen</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[55px] text-center align-middle">Moy.Ex<br/>(60%)</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[50px] text-center align-middle">Crédits</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[55px] text-center align-middle">Moy.ECUE</th>
              <th className="border border-gray-400 px-1 py-1.5 w-[80px] text-center align-middle">Décision</th>
            </tr>
          </thead>
          <tbody>
            {ues.map((ue) => (
              <React.Fragment key={ue.ue_id}>
                {ue.matieres.map((m) => {
                  const moyD = m.moy_devoirs;
                  const moyDPond = moyD !== null && moyD !== undefined ? moyD * 0.4 : null;
                  const noteEx = m.note_examen;
                  const moyExPond = noteEx !== null && noteEx !== undefined ? noteEx * 0.6 : null;
                  const aRattrapage = m.note_examen_rattrapage !== null && m.note_examen_rattrapage !== undefined;
                  return (
                    <tr key={m.matiere_id} className="border-b border-gray-300">
                      <td className="border border-gray-400 px-2 py-1 align-middle">{m.nom_matiere}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">{m.absent_d1 ? 'ABS' : fmt(m.note_d1)}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">{m.absent_d2 ? 'ABS' : fmt(m.note_d2)}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">{fmt(moyDPond)}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">
                        <div className="font-semibold">
                          {m.absent_examen ? 'ABS' : fmt(noteEx)}
                        </div>
                        {aRattrapage && (
                          <div className="text-[8px] text-gray-600 leading-tight mt-0.5">
                            SO: {m.absent_examen_s1 ? 'ABS' : (m.note_examen_s1 !== null && m.note_examen_s1 !== undefined ? fmt(m.note_examen_s1) : '—')}
                            <br/>
                            SR: {m.absent_examen_rattrapage ? 'ABS' : fmt(m.note_examen_rattrapage)}
                          </div>
                        )}
                      </td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">{fmt(moyExPond)}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center align-middle">{fmt(m.credits, 2)}</td>
                      <td className="border border-gray-400 px-1 py-1 text-center font-semibold align-middle">{fmt(m.moyenne_ecue)}</td>
                      <td className="border border-gray-400 px-1 py-1 align-middle"></td>
                    </tr>
                  );
                })}
                {/* Ligne UE total : afficher les crédits TOTAUX de l'UE (pas seulement obtenus) */}
                {/* Fix 2026-09-10 : afficher le nom UE au lieu du code technique */}
                <tr className={ue.validee ? 'bg-blue-100' : 'bg-orange-100'}>
                  <td className="border border-gray-400 px-2 py-1.5 font-bold align-middle">
                    {(() => {
                      const nom = (ue.nom_ue || '').trim();
                      if (/^UE\s?\d/i.test(nom)) return nom;
                      return nom ? `UE ${nom}` : (ue.code_ue || '');
                    })()}
                  </td>
                  <td className="border border-gray-400 align-middle" colSpan={5}></td>
                  <td className="border border-gray-400 px-1 py-1.5 text-center font-bold align-middle">{fmt(ue.credits_total, 2)}</td>
                  <td className="border border-gray-400 px-1 py-1.5 text-center font-bold align-middle">{fmt(ue.moyenne_ue)}</td>
                  <td className="border border-gray-400 px-1 py-1.5 text-center font-bold align-middle">
                    {(() => {
                      if (!ue.validee) return 'Non validé';
                      if (ue.session_label === 'compensation') return 'Validé (comp.)';
                      if (ue.session_label === 'rattrapage') return 'Validé (rattrap.)';
                      // Fallback rétrocompatibilité
                      if (ue.validee_par_compensation) return 'Validé (comp.)';
                      if (ue.validee_en_rattrapage) return 'Validé (rattrap.)';
                      return 'Validé';
                    })()}
                  </td>
                </tr>
              </React.Fragment>
            ))}
            {/* Ligne moyenne générale */}
            <tr>
              <td className="border border-gray-400 px-2 py-1.5 align-middle" colSpan={7}></td>
              <td className="border border-gray-400 px-1 py-1.5 text-center font-bold bg-gray-100 align-middle">{fmt(moyenneFinale)}</td>
              <td className="border border-gray-400 px-1 py-1.5 align-middle"></td>
            </tr>
          </tbody>
        </table>

        {/* Bloc résultats */}
        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <div className="bg-orange-200 px-3 py-1.5 font-bold text-[11px] w-[180px] text-center flex items-center justify-center">MOYENNE GENERALE</div>
            <div className="border border-gray-400 px-3 py-1.5 font-bold w-[80px] text-center bg-white flex items-center justify-center">{fmt(moyenneFinale)}</div>
            <div className="bg-orange-200 px-3 py-1.5 font-bold text-[11px] w-[60px] text-center flex items-center justify-center">RANG</div>
            <div className="border border-gray-400 px-3 py-1.5 font-bold w-[80px] text-center bg-white flex items-center justify-center">
              {res?.rang ? `${res.rang}${res.effectif_classe ? '/' + res.effectif_classe : ''}` : '-'}
            </div>
          </div>
          <div></div>

          <div className="flex items-center gap-2">
            <div className="bg-orange-200 px-3 py-1.5 font-bold text-[11px] w-[180px] text-center flex items-center justify-center">NOMBRE DE CREDITS</div>
            <div className="border border-gray-400 px-3 py-1.5 font-bold w-[80px] text-center bg-white flex items-center justify-center">
              {fmt(res?.credits_obtenus ?? null, 2)}
            </div>
          </div>
          <div></div>

          <div className="flex items-center gap-2">
            <div className="bg-orange-200 px-3 py-1.5 font-bold text-[11px] w-[180px] text-center flex items-center justify-center">MENTION</div>
            <div className="border border-gray-400 px-3 py-1.5 font-bold w-[200px] text-center bg-white flex items-center justify-center">{mention || '-'}</div>
          </div>
          <div></div>

          <div className="flex items-center gap-2">
            <div className="bg-orange-200 px-3 py-1.5 font-bold text-[11px] w-[180px] text-center flex items-center justify-center">DECISION</div>
            <div className={`px-3 py-1.5 font-bold w-[200px] text-center flex items-center justify-center ${valide ? 'bg-green-200' : 'bg-red-200'}`}>
              {valide ? 'SEMESTRE VALIDE' : 'SEMESTRE NON VALIDE'}
            </div>
          </div>
          <div className="text-right text-[11px] pt-1">
            <div>Fait à {infoVille}, le {dateImpression}</div>
            <div className="mt-1 italic">{chefTitre}</div>
            {isBZ && chefNom && <div className="mt-6 font-bold not-italic">{chefNom}</div>}
          </div>
        </div>

        {/* Mention provisoire en bas */}
        <div className="mt-4 text-center text-[10px] italic text-red-700 border-t border-dashed border-red-300 pt-2">
          Ce relevé est PROVISOIRE et susceptible d'être modifié après délibération définitive du jury.
        </div>
      </div>
    </div>
  );
};
